import { z } from 'zod';

const INTERACTIVE_SHELL_DISPLAY = 'interactive shell';

const OptionalTextSchema = z.string().trim().min(1).optional().catch(undefined);

const TerminalDisplayInputSchema = z.object({
  command: z.string().min(1).optional().catch(undefined),
  description: OptionalTextSchema,
  action: z
    .enum(['open', 'send', 'read', 'interrupt', 'close'])
    .optional()
    .catch(undefined),
  sessionId: OptionalTextSchema,
});

type TerminalDisplayInput = z.infer<typeof TerminalDisplayInputSchema>;

const parseTerminalDisplayInput = (
  input: unknown,
): TerminalDisplayInput | undefined => {
  const parsed = TerminalDisplayInputSchema.safeParse(input);

  return parsed.success ? parsed.data : undefined;
};

const formatInteractiveSessionAction = ({
  action,
  sessionId,
}: TerminalDisplayInput): string | undefined => {
  if (!action) {
    return undefined;
  }

  if (action === 'open') {
    return INTERACTIVE_SHELL_DISPLAY;
  }

  return sessionId ? `${action} ${sessionId}` : action;
};

export const getTerminalDisplayInput = (input: unknown): string | undefined => {
  const parsed = parseTerminalDisplayInput(input);

  if (!parsed) {
    return undefined;
  }

  return parsed.command ?? formatInteractiveSessionAction(parsed);
};

export const getTerminalDescription = (input: unknown): string | undefined =>
  parseTerminalDisplayInput(input)?.description;

export const extractCommandFromArtifactContent = (
  actionContent: string | null | undefined,
): string | undefined => {
  if (!actionContent) {
    return undefined;
  }

  const trimmed = actionContent.trim();

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return actionContent;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    const command = getTerminalDisplayInput(parsed);

    if (command) {
      return command;
    }
  } catch {
    return actionContent;
  }

  return actionContent;
};
